import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Game.modules.css"; // スタイルを分離して管理
import { checkInput, correctInput } from "../components/romajiUtils";
import LiverWindow from "../components/LiverWindow";
import PlayerBrain from "../components/PlayerBrain";
import Comment from "../components/Comment";
import { GameCommentBot, GameHintCommentBot, AnswerCommentBot } from "../components/CommentBot";
import { GAME, SUPER_CHAT } from "../constants/gameConstant"
import { QUESTIONS } from "../constants/questionConstant";
import { LIVER_IMG, LIVER_MESSAGE } from "../constants/liverConstant";

const Game = () => {
  // 現在の問題を管理するステート
  const [currentQuestion, setCurrentQuestion] = useState({});
  // 現在何問目かを管理するステート
  const [questionNo, setQuestionNo] = useState(0);
  // ユーザーが入力中の文字列を管理するステート
  const [userInput, setUserInput] = useState("");
  // ユーザーのスコアを管理するステート
  const [score, setScore] = useState(0);
  // 問題ごとの残り時間を管理するステート
  const [quizTimeLeft, setQuizTimeLeft] = useState(GAME.TIME_PER_QUESTION);
  // スーパーチャット額を管理するステート
  const [superChatPrice, setSuperChatPrice] = useState(0);
  // ゲームが進行中かどうかを管理するステート
  const [isPlaying, setIsPlaying] = useState(false);
  // 入力を受け付けるかどうかを管理するステート
  const [isAcceptInput, setIsAcceptInput] = useState(false);
  // 画面遷移用のフック
  const navigate = useNavigate();
  // 参照保持するフック
  const ref = useRef();
  // 配信者のメッセージを管理するステート
  const [message, setMessage] = useState("");
  // 配信者の画像を管理するステート
  const [liverImage, setLiverImage] = useState(LIVER_IMG.NORMAL)
  // スコアのスーパーチャット額を記録する配列
  const [scoreList, setScoreList] = useState([]);
  // タイピングの内容を記録する配列
  const [typingList, setTypingList] = useState([]);

  // クイズごとの残り時間のカウントダウンを管理するエフェクト
  useEffect(() => {
    if (isPlaying && quizTimeLeft > 0) {
      // 1秒ごとに`quizTimeLeft`を減少させる
      const timer = setTimeout(() => setQuizTimeLeft((time) => time - 1), 1000);
      // 問題の残り秒数に応じてスパチャ額が変化
      if (quizTimeLeft >= SUPER_CHAT.TIME_LEVEL_7) {
        setSuperChatPrice(SUPER_CHAT.PRICE_LEVEL_7);
      } else if (quizTimeLeft >= SUPER_CHAT.TIME_LEVEL_6) {
        setSuperChatPrice(SUPER_CHAT.PRICE_LEVEL_6);
      } else if (quizTimeLeft >= SUPER_CHAT.TIME_LEVEL_5) {
        setSuperChatPrice(SUPER_CHAT.PRICE_LEVEL_5);
      } else if (quizTimeLeft >= SUPER_CHAT.TIME_LEVEL_4) {
        setSuperChatPrice(SUPER_CHAT.PRICE_LEVEL_4);
      } else if (quizTimeLeft >= SUPER_CHAT.TIME_LEVEL_3) {
        setSuperChatPrice(SUPER_CHAT.PRICE_LEVEL_3);
      } else if (quizTimeLeft >= SUPER_CHAT.TIME_LEVEL_2) {
        setSuperChatPrice(SUPER_CHAT.PRICE_LEVEL_2);
      } else if (quizTimeLeft >= SUPER_CHAT.TIME_LEVEL_1) {
        setSuperChatPrice(SUPER_CHAT.PRICE_LEVEL_1);
      } else {
        setSuperChatPrice(0);
      }

      // 問題の残り秒数に応じてヒントコメントを出力する
      GameHintCommentBot({ comment: ref.current, question: currentQuestion, time: quizTimeLeft });

      // クリーンアップ関数: コンポーネントがアンマウントされたり依存が変わるときにタイマーを解除
      return () => clearTimeout(timer);
    } else if (quizTimeLeft === 0) {
      if (isAcceptInput) {
        // ユーザーの回答が制限時間内にされなかった
        timeoutUserAnswer();
      } else {
        // 正解／不正解の後のタイムアウト
        nextQuestion();
      }
    }
  }, [quizTimeLeft, isPlaying]);

  // スパチャ額が変更されたときの処理
  useEffect(() => {
    // 時間に応じてコメントゾーンの入力欄の色を変える
    ref.current?.setCommentInputColor(quizTimeLeft, superChatPrice);
  }, [superChatPrice]);

  // コンポーネントマウント時にゲームを自動開始するエフェクト
  useEffect(() => {
    if (!isPlaying) {
      // ゲームが開始されていない場合に自動で開始
      startGame();
    }
  }, [isPlaying]);

  // ゲームを開始する関数
  const startGame = () => {
    setIsPlaying(true); // ゲーム状態を開始に設定
    setQuizTimeLeft(GAME.TIME_PER_QUESTION); // 残り時間をリセット
    setScore(0); // スコアをリセット
    setIsAcceptInput(true); // 入力を受け付ける
    setUserInput(""); // ユーザー入力をクリア
    setQuestionNo(0); // 問題を一問目にリセット
    setCurrentQuestion(QUESTIONS[questionNo]); //問題を設定
    setMessage(QUESTIONS[0].QUESTION); //ランダムなメッセージを設定
    setScoreList([]);
    setTypingList([]);

    // 汎用コメントBOTの開始
    GameCommentBot({ comment: ref.current });
  };

  // ユーザーの回答が時間切れ
  const timeoutUserAnswer = () => {
    // コメントゾーン入力欄の状態を変更
    ref.current?.setUserTypingState(false);

    // 回答ボットにコメントさせる
    AnswerCommentBot({ comment: ref.current, question: currentQuestion });

    setScoreList((scoreList) => [...scoreList, 0]); //スコアを記録
    setTypingList((typingList) => [...typingList, ""]);//タイピング内容の記録
    setIsAcceptInput(false); // 入力を受け付けない
    // ライバー：不正解のリアクション
    setLiverAction(false);
  };

  // 次の問題に移る
  const nextQuestion = () => {
    const nextQuestionNo = questionNo + 1;
    // 用意した問題数を超えたらゲームを終了
    if (nextQuestionNo >= QUESTIONS.length) {
      setTimeout(() => {
        setIsPlaying(false); // ゲーム状態を終了に設定
      }, 5000);
      setMessage("お疲れ様でした～\nまた次回配信で会いましょう\nおつたかでした");
      setLiverImage(LIVER_IMG.GOODBYE);
      return;
    }
    setIsAcceptInput(true); // 入力を受け付ける
    setUserInput(""); // 入力フィールドをクリア
    setQuestionNo(nextQuestionNo); // 次の問題Noをセット
    setCurrentQuestion(QUESTIONS[nextQuestionNo]); // 新しい問題を設定
    setQuizTimeLeft(GAME.TIME_PER_QUESTION); // 問題ごとの時間経過をリセット

    ref.current?.setUserTypingState(true);// コメントゾーン入力欄の状態を変更

    setMessage(QUESTIONS[questionNo + 1]?.QUESTION); //新しいメッセージを設定
    setLiverImage(LIVER_IMG.NORMAL);
  };

  // ゲームを終了し結果画面に遷移する関数
  const endGame = () => {
    // 結果画面にスコアをURLパラメータとして渡して遷移
    navigate(`/ending?score=${score}`, {
      state: {
        scoreList: scoreList,
        typingList: typingList,
      }
    });
  };

  //配信者のメッセージ、画像を変更する
  const setLiverAction = (isCorrectComment) => {
    setIsAcceptInput(false); // 入力を受け付けない

    //特定の問題（挨拶など）の場合処理を抜ける
    if (QUESTIONS[questionNo]?.ID === 1 || QUESTIONS[questionNo]?.ID === 2 ||
      QUESTIONS[questionNo]?.ID === 6 || QUESTIONS[questionNo]?.ID === 10) {
      setQuizTimeLeft(2); // クイズの残り時間を2秒に設定
      return;
    }

    setQuizTimeLeft(5); // クイズの残り時間を5秒に設定

    //問題が正解していた場合の処理
    if (isCorrectComment) {
      switch (superChatPrice) {
        case SUPER_CHAT.PRICE_LEVEL_0:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_1);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_0);
          break;
        case SUPER_CHAT.PRICE_LEVEL_1:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_1);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_1);
          break;
        case SUPER_CHAT.PRICE_LEVEL_2:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_2);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_2);
          break;
        case SUPER_CHAT.PRICE_LEVEL_3:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_3);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_3);
          break;
        case SUPER_CHAT.PRICE_LEVEL_4:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_4);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_4);
          break;
        case SUPER_CHAT.PRICE_LEVEL_5:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_5);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_5);
          break;
        case SUPER_CHAT.PRICE_LEVEL_6:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_6);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_6);
          break;
        case SUPER_CHAT.PRICE_LEVEL_7:
          setMessage(LIVER_MESSAGE.SUPER_CHAT_LEVEL_7);
          setLiverImage(LIVER_IMG.SUPER_CHAT_LEVEL_7);
          break;
      }
    }
    //問題が不正解の場合の処理
    else {
      setMessage(LIVER_MESSAGE.INCORRECT);
      setLiverImage(LIVER_IMG.INCORRECT_ANSWER);
    }
  }

  // ユーザーの入力イベントを処理する関数
  const handleInputChange = (e) => {
    let newInput = e.target.value;
    // 通常のタイピングモード
    if (currentQuestion.TYPE == "normal") {
      if (checkInput(currentQuestion.ANSWER_ROM, newInput)) {
        newInput = correctInput(currentQuestion.ANSWER_ROM, newInput);
        setUserInput(newInput);
        if (newInput.length === currentQuestion.ANSWER_ROM.length) {
          // コメントの追加処理
          ref.current?.addComment(
            GAME.PLAYER_NAME,
            currentQuestion.ANSWER_JP,
            superChatPrice
          );
          ref.current?.setUserTypingState(false);// コメントゾーン入力欄の状態を変更

          setScore((score) => score + superChatPrice); // スコアを増加
          setScoreList((scoreList) => [...scoreList, superChatPrice]);//スコアを記録
          setTypingList((typingList) => [...typingList, currentQuestion.ANSWER_JP]);//タイピング内容の記録
          // ライバー：正解のリアクション
          setLiverAction(true);
        }
      }
      // ３択のタイピングモード
    } else if (currentQuestion.TYPE == "quiz") {
      let isInputSuccess = [false, false, false];
      //選択肢1の入力確認
      if (checkInput(currentQuestion.OPTION_1_ROM, newInput)) {
        newInput = correctInput(currentQuestion.OPTION_1_ROM, newInput);
        isInputSuccess[0] = true;
      }
      //選択肢2の入力確認
      if (checkInput(currentQuestion.OPTION_2_ROM, newInput)) {
        newInput = correctInput(currentQuestion.OPTION_2_ROM, newInput);
        isInputSuccess[1] = true;
      }
      //選択肢3の入力確認
      if (checkInput(currentQuestion.OPTION_3_ROM, newInput)) {
        newInput = correctInput(currentQuestion.OPTION_3_ROM, newInput);
        isInputSuccess[2] = true;
      }
      //１つでも選択肢と入力が一致している場合
      if (isInputSuccess.some((value) => value)) {
        setUserInput(newInput);
        // 正しい選択肢を選んだ場合
        if (newInput === currentQuestion.ANSWER_ROM) {
          // コメントの追加処理
          ref.current?.addComment(
            GAME.PLAYER_NAME,
            currentQuestion.ANSWER_JP,
            superChatPrice
          );
          ref.current?.setUserTypingState(false);// コメントゾーン入力欄の状態を変更

          setScore((score) => score + superChatPrice); // スコアを増加
          setScoreList((scoreList) => [...scoreList, superChatPrice]); //スコアを記録
          setTypingList((typingList) => [...typingList, currentQuestion.ANSWER_JP]);//タイピング内容の記録
          // ライバー：正解のリアクション
          setLiverAction(true);
        } else if (
          newInput === currentQuestion.OPTION_1_ROM ||
          newInput === currentQuestion.OPTION_2_ROM ||
          newInput === currentQuestion.OPTION_3_ROM
        ) {
          //間違った選択肢を選んだ場合
          if (isInputSuccess.indexOf(true) !== -1) {
            let choiceNum = "OPTION_" + (isInputSuccess.indexOf(true) + 1) + "_JP";
            ref.current?.addComment(
              GAME.PLAYER_NAME,
              currentQuestion[choiceNum],
              superChatPrice
            );
            setTypingList((typingList) => [...typingList, currentQuestion.ANSWER_JP]);//タイピング内容の記録
          }
          ref.current?.setUserTypingState(false);// コメントゾーン入力欄の状態を変更

          //スコアを記録
          setScoreList((scoreList) => [...scoreList, superChatPrice]);

          // ライバー：不正解のリアクション
          setLiverAction(false);
        }
      }
    }
  };

  return (
    <div className="game-screen">
      {
        // ゲーム中の場合の表示
        <>
          <LiverWindow img={liverImage} text={message} />

          {isAcceptInput && (
            /* プレイヤーの入力欄 */
            <PlayerBrain
              currentQuestion={currentQuestion}
              userInput={userInput}
              handleInputChange={handleInputChange}
            />
          )}

          {/* コメント欄 */}
          <Comment userInput={userInput} ref={ref} />

          {/* 現在のスコアを表示 */}
          <div class="game_status">
            <h2>
              現在のスコア: {score}円
            </h2>
          </div>
        </>
      }
      {!isPlaying ? (
        <>
          {endGame()}
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default Game;
